import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/auth/AuthContext";

const STATUSES = ["new", "under_review", "contacted", "approved", "declined"];

const BADGE = {
  new: "bg-[#d4af37]/15 text-[#d4af37] border-[#d4af37]/30",
  under_review: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  contacted: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  estimate_sent: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  declined: "bg-zinc-500/15 text-zinc-500 border-zinc-600/30",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function detailRows(app) {
  const skip = new Set(["experience", "description"]);
  const rows = Object.entries(app.data)
    .filter(([k, v]) => !skip.has(k) && v && (!Array.isArray(v) || v.length))
    .map(([k, v]) => [k.replace(/_/g, " "), Array.isArray(v) ? v.join(", ") : String(v)]);
  return rows;
}

export default function ApplicationsPanel() {
  const { logout, authHeaders } = useAuth();
  const [kind, setKind] = useState("technician");
  const [q, setQ] = useState("");
  const [apps, setApps] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [estimates, setEstimates] = useState({});

  const load = (k = kind, query = q) => {
    const params = new URLSearchParams();
    params.set("kind", k);
    if (query) params.set("q", query);
    axios.get(`${API}/admin/applications?${params}`, { headers: authHeaders() })
      .then((r) => setApps(r.data))
      .catch((err) => {
        if (err.response?.status === 401) logout();
        else toast.error("Could not load applications.");
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setApps(null); load(kind, q); }, [kind]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/applications/${id}`, { status }, { headers: authHeaders() });
      setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
      toast.success(`Status: ${status.replace("_", " ")}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const sendEstimate = async (id) => {
    const draft = estimates[id] || {};
    if (!draft.amount) {
      toast.error("Enter an estimate amount first.");
      return;
    }
    try {
      const { data } = await axios.patch(
        `${API}/admin/applications/${id}`,
        { status: "estimate_sent", estimate_amount: Number(draft.amount), estimate_note: draft.note || "" },
        { headers: authHeaders() }
      );
      setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
      toast.success("Estimate emailed to the customer.");
    } catch {
      toast.error("Could not send estimate.");
    }
  };

  const exportCsv = async () => {    try {
      const r = await axios.get(`${API}/admin/applications-export?kind=${kind}`, {
        headers: authHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `durotech28-${kind}-applications.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const title = (a) =>
    a.kind === "technician" ? a.data.full_name
      : a.kind === "local_ai" || a.kind === "solar" || a.kind === "concealment" ? a.data.name
      : a.data.company_name;
  const subtitle = (a) =>
    a.kind === "technician"
      ? `${a.data.email} · ${a.data.phone} · ${a.data.city_state}`
      : a.kind === "local_ai"
        ? `${a.data.email} · ${a.data.phone} · ${a.data.ai_service || ""}`
        : a.kind === "solar"
          ? `${a.data.email} · ${a.data.phone} · ${a.data.service || ""}`
          : a.kind === "concealment"
            ? `${a.data.email} · ${a.data.phone} · ${a.data.mode === "onsite" ? "On-site assessment" : "Photo quote"}`
            : `${a.data.contact_person} · ${a.data.business_email} · ${a.data.phone}`;

  return (
    <div data-testid="applications-panel">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {[{ id: "technician", label: "Technicians" }, { id: "partnership", label: "Partnerships" }, { id: "local_ai", label: "Local AI" }, { id: "solar", label: "Solar Leads" }, { id: "concealment", label: "Concealment" }].map((t) => (
          <button key={t.id} type="button"
            onClick={() => setKind(t.id)}
            data-testid={`apps-kind-${t.id}`}
            className={`px-4 py-2 rounded text-[10px] font-mono uppercase tracking-wider transition-colors ${
              kind === t.id
                ? "bg-[#d4af37]/15 border border-[#d4af37] text-[#d4af37]"
                : "border border-zinc-700 text-zinc-400 hover:border-[#d4af37]"
            }`}>
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <input
          type="text" placeholder="Search name, email, skill..." value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="apps-search"
          className="bg-[#0a0a0c] border border-zinc-700 rounded px-3 py-2 text-xs text-white font-mono focus:border-[#d4af37] outline-none w-56"
        />
        <button type="button" onClick={() => load()} data-testid="apps-search-btn"
          className="px-4 py-2 rounded border border-zinc-700 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:border-[#d4af37] transition-colors">
          Search
        </button>
        <button type="button" onClick={exportCsv} data-testid="apps-export-btn"
          className="px-4 py-2 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors">
          Export CSV
        </button>
      </div>

      {apps === null ? (
        <p className="text-zinc-500 font-mono text-xs">Loading...</p>
      ) : apps.length === 0 ? (
        <p className="text-zinc-500 font-mono text-xs py-10 text-center" data-testid="apps-empty">No applications yet.</p>
      ) : (
        <div className="space-y-3">
          {apps.map((a) => (
            <div key={a.id} className="rounded-lg bg-[#121216] border border-zinc-800 px-4 py-4" data-testid={`app-row-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <button type="button" className="text-left min-w-[220px]" onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  data-testid={`app-expand-${a.id}`}>
                  <p className="text-sm text-white font-medium">{title(a)}</p>
                  <p className="text-[11px] text-[#d4af37] font-mono mt-0.5">{subtitle(a)}</p>
                  <p className="text-[10px] text-zinc-500 font-mono mt-1">{fmtDate(a.created_at)} · tap to {expanded === a.id ? "collapse" : "expand"}</p>
                </button>
                <span className={`px-2.5 py-1 rounded border text-[10px] font-mono uppercase tracking-wider ${BADGE[a.status] || BADGE.new}`}
                  data-testid={`app-status-${a.id}`}>
                  {a.status.replace("_", " ")}
                </span>
              </div>

              {expanded === a.id && (
                <div className="mt-4 border-t border-zinc-800 pt-4 space-y-1.5" data-testid={`app-details-${a.id}`}>
                  {detailRows(a).map(([k, v]) => (
                    <p key={k} className="text-[11px] font-mono"><span className="text-zinc-500 uppercase">{k}:</span> <span className="text-zinc-300">{v}</span></p>
                  ))}
                  {(a.data.experience || a.data.description) && (
                    <p className="text-[11px] font-mono text-zinc-300 border-l-2 border-[#d4af37]/40 pl-2 mt-2">{a.data.experience || a.data.description}</p>
                  )}
                  {a.files?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {a.files.map((f) => (
                        <a key={f.file_id} href={f.link} target="_blank" rel="noreferrer"
                          data-testid={`app-file-${f.file_id}`}
                          className="block text-[11px] font-mono text-[#d4af37] hover:underline">
                          ↓ {f.original_filename} ({Math.round(f.size / 1024)} KB)
                        </a>
                      ))}
                    </div>
                  )}
                  {a.kind === "concealment" && (
                    <div className="mt-4 rounded-lg bg-[#0a0a0c] border border-[#d4af37]/25 p-4" data-testid={`estimate-box-${a.id}`}>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-2">Custom Estimate</p>
                      {a.data.estimate_amount != null && (
                        <p className="text-[11px] font-mono text-zinc-300 mb-2">
                          Current: ${a.data.estimate_amount} {a.data.estimate_note && `— ${a.data.estimate_note}`}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <input type="number" placeholder="Amount $" value={estimates[a.id]?.amount ?? ""}
                          onChange={(e) => setEstimates((p) => ({ ...p, [a.id]: { ...p[a.id], amount: e.target.value } }))}
                          data-testid={`estimate-amount-${a.id}`}
                          className="bg-[#121216] border border-zinc-700 rounded px-3 py-2 text-xs text-white font-mono w-28 focus:border-[#d4af37] outline-none" />
                        <input type="text" placeholder="Note to customer (optional)" value={estimates[a.id]?.note ?? ""}
                          onChange={(e) => setEstimates((p) => ({ ...p, [a.id]: { ...p[a.id], note: e.target.value } }))}
                          data-testid={`estimate-note-${a.id}`}
                          className="bg-[#121216] border border-zinc-700 rounded px-3 py-2 text-xs text-white font-mono flex-1 min-w-[160px] focus:border-[#d4af37] outline-none" />
                        <button type="button" onClick={() => sendEstimate(a.id)} data-testid={`estimate-send-${a.id}`}
                          className="px-4 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors">
                          Send Estimate
                        </button>
                      </div>
                      <p className="text-[9px] font-mono text-zinc-600 mt-2">Emails the customer instantly. They approve by reply or phone — then mark Approved.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">Move to</span>
                {STATUSES.filter((s) => s !== a.status).map((s) => (
                  <button key={s} type="button" onClick={() => setStatus(a.id, s)}
                    data-testid={`app-set-${s}-${a.id}`}
                    className="px-2 py-1 rounded border border-zinc-700 text-[9px] font-mono uppercase tracking-wider text-zinc-400 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors">
                    {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
